import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PRIVACY_POLICY_VERSION } from '../utils/privacy';

/**
 * In-app privacy policy -- a STRUCTURE awaiting the institute's legal text.
 *
 * Both app stores require a publicly reachable policy URL before submission,
 * and Apple's App Privacy label has to be written from a real source document.
 * The headings below follow what those two forms ask about, so the final text
 * drops into a shape reviewers expect. Until it does, the banner says this is
 * a draft: presenting placeholder copy as a binding policy would be worse than
 * having none.
 *
 * Public, outside PrivateRoute: someone deciding whether to register has to be
 * able to read it before they have an account.
 */
const SECTIONS = ['who', 'collect', 'use', 'share', 'retain', 'rights', 'contact'];

export default function PrivacyPolicy() {
  const { t } = useTranslation('account');
  const navigate = useNavigate();

  return (
    <main style={styles.page}>
      <article style={styles.card}>
        <h1 style={styles.h1}>{t('privacyHeading')}</h1>
        <p style={styles.version}>{t('privacyVersion', { version: PRIVACY_POLICY_VERSION })}</p>
        <p role="note" style={styles.draft}>{t('privacyDraftBanner')}</p>
        {SECTIONS.map(key => (
          <section key={key} style={styles.section}>
            <h2 style={styles.h2}>{t(`sec_${key}Title`)}</h2>
            <p style={styles.body}>{t(`sec_${key}Body`)}</p>
          </section>
        ))}
        <button type="button" style={styles.back} title={t('tip.privacyBack')}
                onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/login'))}>
          {t('back')}
        </button>
      </article>
    </main>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--c-bg-subtle-2)', padding: 16, display: 'flex', justifyContent: 'center' },
  card: { background: 'var(--c-surface)', borderRadius: 12, padding: '24px 28px', maxWidth: 720, width: '100%',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)', lineHeight: 1.65 },
  h1: { fontSize: 24, margin: '0 0 4px' },
  version: { fontSize: 13, color: 'var(--c-text-muted-fg)', margin: '0 0 12px' },
  draft: { fontSize: 13, color: 'var(--c-warning-text-fg)', background: 'var(--c-warning-soft-2)', border: '1px solid var(--c-warning)',
           borderRadius: 6, padding: '8px 12px' },
  section: { marginTop: 18 },
  h2: { fontSize: 16, margin: '0 0 4px' },
  body: { fontSize: 14, color: 'var(--c-text-body-fg)', margin: 0 },
  back: { marginTop: 24, background: 'var(--c-primary)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6,
          padding: '9px 18px', fontWeight: 600, cursor: 'pointer' },
};
